/* eslint-disable jsx-a11y/label-has-for */
import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';

import UIImage from './ui/UIImage';

/*
 * A single banner spanning the full width of the ads row. Field names match the
 * Image block on purpose (imageUrl / imageUrlMobile / description / link) so
 * the media manager fills them the same way, and `description` doubles as the
 * alt text exactly as it does there.
 */
const WideBanner = ({
  activeLanguage, handlerSetComponents, components, languages, id, reusableComponent, reusableComponentSlugname,
}) => {
  const initialState = {
    type: 'WideBanner',
    imageUrl: { en: '' },
    imageUrlMobile: { en: '' },
    description: { en: '' },
    link: null,
  };

  const [index, setIndex] = useState();
  const [state, setState] = useState(initialState);

  const [imageUrl, setImageUrl] = useState({ en: '' });
  const [imageUrlMobile, setImageUrlMobile] = useState({ en: '' });
  const [description, setDescription] = useState({ en: '' });
  const [link, setLink] = useState(null);

  useEffect(() => {
    const componentIndex = components.findIndex(obj => obj.id === id);
    const { component } = components[componentIndex];

    if (reusableComponent) {
      setState(reusableComponent);
      if (!component) handlerSetComponents({ component: { key: reusableComponentSlugname } }, componentIndex);
      return;
    }

    setIndex(componentIndex);

    if (component) {
      setState(component);
      setImageUrl(component.imageUrl || { en: '' });
      setImageUrlMobile(component.imageUrlMobile || { en: '' });
      setDescription(component.description || { en: '' });
      setLink(component.link || null);
    } else {
      handlerSetComponents({ id, component: state }, componentIndex);
    }
  }, [components]);

  useEffect(() => {
    const componentIndex = components.findIndex(obj => obj.id === id);
    const construction = {
      type: state.type,
      compId: state.compId || '',
      imageUrl,
      imageUrlMobile,
      description,
      link,
    };

    handlerSetComponents({ component: construction }, componentIndex);
  }, [imageUrl, imageUrlMobile, description, link]);

  // UIImage mutates the object it is given, so hand each setter a fresh one:
  // writing back the same reference makes React bail out and the pick is lost.
  const setImage = setter => obj => setter({ ...obj });

  return (
    <div className="w-full text-black">
      <div className="flex">
        <div className="flex-1 pr-2">
          <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2">
            Desktop Image
          </label>
          <UIImage
            imageUrl={imageUrl}
            setImageUrl={setImage(setImageUrl)}
            activeLanguage={activeLanguage}
            description={description}
            setDescription={setDescription}
          />
        </div>
        <div className="flex-1 pl-2">
          <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2">
            Mobile Image
          </label>
          <UIImage
            imageUrl={imageUrlMobile}
            setImageUrl={setImage(setImageUrlMobile)}
            activeLanguage={activeLanguage}
            description={description}
            setDescription={setDescription}
          />
        </div>
      </div>

      {languages.map(lang => (
        <div key={lang} className={activeLanguage.toLowerCase() === lang.toLowerCase() ? 'block w-full' : 'hidden'}>
          <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 mt-4">
            Alt text (describes the image for screen readers and when it fails to load)
          </label>
          <input
            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            type="text"
            value={description[lang.toLowerCase()] || ''}
            onChange={e => setDescription({ ...description, [lang.toLowerCase()]: e.target.value })}
          />
        </div>
      ))}

      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
        Link (if internal just use: /artista/interpol)
      </label>
      <input
        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
        type="text"
        value={link || ''}
        onChange={e => setLink(e.target.value || null)}
      />
    </div>
  );
};

WideBanner.propTypes = {
  activeLanguage: PropTypes.string.isRequired,
  handlerSetComponents: PropTypes.func.isRequired,
  components: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  id: PropTypes.string.isRequired,
  languages: PropTypes.arrayOf(PropTypes.string).isRequired,
  reusableComponent: PropTypes.shape(),
};

WideBanner.defaultProps = {
  reusableComponent: null,
};

export default WideBanner;
